import { useAppState, useAppDispatch } from "@/store/useApp";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GroupSuggestionItem } from "./GroupSuggestionItem";
import { usePendingSuggestions } from "@/hooks/use-pending-suggestions";
import { useNavigate, useParams } from "react-router";
import { SquareKanban } from "lucide-react";

export const GroupSuggestions = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { documentId } = useParams();
  const currentDocument = useAppState((state) => state.currentDocument);
  const selectedSuggestionIndex = useAppState(
    (state) => state.selectedSuggestionIndex
  );
  const selectedGroup = useAppState((state) => state.selectedGroup);
  const pendingSuggestions = usePendingSuggestions();

  const handleSuggestionProcess = (processedIndex: number) => {
    // After processing, we need to determine the new selection state
    const remainingSuggestionsCount = pendingSuggestions.length - 1; // One less after processing

    if (remainingSuggestionsCount === 0) {
      // No suggestions remaining, select last block in group
      if (selectedGroup && selectedGroup.blockIds.length > 0) {
        dispatch({
          type: "CLEAR_SUGGESTION_SELECT_BLOCK",
          payload: selectedGroup.blockIds.length - 1,
        });
      } else {
        dispatch({
          type: "SET_SELECTED_SUGGESTION_INDEX",
          payload: null,
        });
      }
    } else if (processedIndex === pendingSuggestions.length - 1) {
      // Processed the last suggestion but others remain, select the new last suggestion
      dispatch({
        type: "SET_SELECTED_SUGGESTION_INDEX",
        payload: remainingSuggestionsCount - 1,
      });
    }
    // Otherwise, keep the same index (no change required)
  };

  // Check if we should show the "Review pending suggestions" button
  const shouldShowReviewButton = () => {
    if (!currentDocument || !selectedGroup) return false;

    // User is viewing the last group
    const lastGroup = currentDocument.groups[currentDocument.groups.length - 1];
    if (selectedGroup.id !== lastGroup.id) return false;

    // There are pending suggestions with rubricCriteriaId set
    const hasPendingSuggestionsWithRubric = currentDocument.suggestions.some(
      (suggestion) =>
        suggestion.status === "pending" && suggestion.rubricCriteriaId
    );

    return hasPendingSuggestionsWithRubric;
  };

  const handleReviewClick = () => {
    if (documentId) {
      navigate(`/documents/${documentId}/rubric`);
    }
  };

  console.log("Should show review button: ", shouldShowReviewButton());

  if (shouldShowReviewButton() && pendingSuggestions.length === 0) {
    return (
      <div className="flex justify-center mt-8">
        <Button onClick={handleReviewClick} variant="secondary">
          Review pending suggestions
          <SquareKanban className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  if (pendingSuggestions.length === 0) return null;

  return (
    <>
      <div
        className="flex mt-10 mb-2 justify-center items-center mr-12"
        data-testid="ai-suggestions"
      >
        <div className="border-zinc-200 border w-[100px] mr-8"></div>
        <span className="text-xs text-muted-foreground">Additional</span>
        <Badge
          variant="secondary"
          className="bg-purple-50 text-purple-600 mx-1"
        >
          AI
        </Badge>
        <span className="text-xs text-muted-foreground">remarks</span>
        <div className="border-zinc-200 border w-[100px] ml-8"></div>
      </div>

      <div className="mt-8 ml-[-32px] pl-10 pr-8 py-8">
        <div className="space-y-6">
          {pendingSuggestions.map((suggestion, index) => (
            <GroupSuggestionItem
              key={suggestion.id}
              suggestion={suggestion}
              isSelected={selectedSuggestionIndex === index}
              suggestionIndex={index}
              onProcess={handleSuggestionProcess}
            />
          ))}
        </div>
      </div>
    </>
  );
};
